import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PhoneOtpLogin from "@/components/auth/PhoneOtpLogin";
import { useNoIndex } from "@/hooks/use-no-index";

// Same toggle the Register wizard uses — keep them in lock-step.
const PHONE_AUTH_ENABLED = (() => {
  const v = process.env.REACT_APP_PHONE_AUTH_ENABLED;
  if (v == null || v === "") return true;
  return !["false", "0", "off", "no"].includes(String(v).toLowerCase());
})();

/**
 * Owner-facing login. When Phone Auth is enabled, OTP is the primary flow with
 * an email-fallback toggle. When disabled (e.g. Firebase API key suspended),
 * the email flow is the only option and the toggle is hidden.
 */
export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // Default to email when phone-auth is disabled; otherwise phone-first.
  const [mode, setMode] = useState(PHONE_AUTH_ENABLED ? "phone" : "email");
  const [f, setF] = useState({ email: loc.state?.email || "", password: "" });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { document.title = "Login - Pune Spa Guide"; }, []);
  useNoIndex();

  // If the user is already authenticated, the login page is meaningless —
  // send them straight to their dashboard. This fixes the "click Login in the
  // header while already signed-in and nothing appears to happen" bug: the
  // header link went to /login, which used to render the form even when a
  // valid session already existed.
  if (user) {
    const dest = user.role === "admin" ? "/admin" : "/dashboard";
    return <Navigate to={loc.state?.from || dest} replace />;
  }

  /** Build a friendly error message from the backend response. */
  const explainError = (err) => {
    if (!err.response) return "Unable to reach the server. Please check your connection.";
    const detail = err.response?.data?.detail;
    if (err.response.status === 401) return "Wrong email or password. Please try again.";
    return detail || "Login failed";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(f.email, f.password);
      toast.success(`Welcome back, ${user.name}!`);
      const dest = user.role === "admin" ? "/admin" : "/dashboard";
      nav(loc.state?.from || dest);
    } catch (err) {
      toast.error(explainError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-4 py-16">
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-8 shadow-[0_12px_40px_rgba(17,24,39,0.06)]">
        <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Spa Owner Portal</div>
        <h1 className="font-heading text-3xl mt-2">Welcome Back</h1>
        <p className="text-sm text-[#4B5563] mt-1">Sign in to manage your spa listing.</p>

        <div className="mt-6">
          {mode === "phone" ? (
            <PhoneOtpLogin onEmailFallbackClick={() => setMode("email")} />
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="email-login-form">
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" required value={f.email} onChange={(e) => setF({...f, email: e.target.value})} data-testid="login-email-input" />
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" required value={f.password} onChange={(e) => setF({...f, password: e.target.value})} data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full h-11 bg-[#111827] hover:bg-[#0B1220] text-white">
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
          )}
        </div>

        {/* Mode toggle — hidden when phone auth is disabled (email-only mode) */}
        {PHONE_AUTH_ENABLED && (
          <div className="mt-5 text-xs text-center text-[#4B5563]">
            {mode === "phone" ? (
              <>
                Have an email account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("email")}
                  className="text-[#1F4E3D] font-medium hover:underline"
                  data-testid="switch-to-email-button"
                >
                  Sign in with email →
                </button>
              </>
            ) : (
              <>
                Prefer phone OTP?{" "}
                <button
                  type="button"
                  onClick={() => setMode("phone")}
                  className="text-[#1F4E3D] font-medium hover:underline"
                  data-testid="switch-to-phone-button"
                >
                  Use phone instead →
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
